import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  useTheme,
} from '@mui/material';
import { Column, Row } from 'Components/Grid';
import { Controller } from 'react-hook-form';
import { ButtonForm } from 'Components/Form/styles';
import { useQueries } from 'react-query';
import { getAssetsShort } from 'services/assets-service';
import { getSystemsShort } from 'services/systems-service';
import { getAreasShort } from 'services/areas-service';
import { getSelectedContent } from 'utils/select_utils';
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
import { getStyles, MenuProps } from './helpers';
import { InputLoader } from 'Components/PageLoader/Input';

export const Vinculos = ({ handleBack, handleComplete, control, handleSubmit }) => {
  const selectedTenant = useSelector(useAuthTenant);
  const theme = useTheme();

  const onSubmit = () => {
    handleComplete();
  };

  const queries = useQueries([
    {
      queryKey: ['getAreas'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return [];
        const result = await getAreasShort(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
    {
      queryKey: ['getAssets'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return [];
        const result = await getAssetsShort(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
    {
      queryKey: ['getSystems'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return [];
        const result = await getSystemsShort(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
  ]);

  const [
    { data: areas, isLoading: areaLoading, isError: areaError },
    { data: assets, isLoading: assetLoading, isError: assetError },
    { data: systems, isLoading: systemLoading, isError: systemError },
  ] = queries;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Row justifyContent="center">
        <Column xs={12} className="d-flex">
          {!areaLoading && !areaError && (
            <Controller
              control={control}
              name="areas"
              render={({ field: { onChange, value }, fieldState }) => {
                // 🔒 garante array
                const safeValue = Array.isArray(value) ? value : [];

                // opcional: não renderizar arquivadas no menu
                const visibleAreas = areas?.filter(a => !a.archived) ?? [];

                const handleDelete = (selected) => {
                  const newValue = safeValue.filter(v => v._id !== selected._id);
                  onChange(newValue);
                };

                return (
                  <FormControl fullWidth sx={{ margin: '0 auto 24px' }} error={!!fieldState.error}>
                    <InputLabel id="areas-label" shrink>
                      Áreas vinculadas
                    </InputLabel>

                    <Select
                      labelId="areas-label"
                      id="areas"
                      multiple
                      notched
                      value={getSelectedContent(visibleAreas, safeValue)}
                      onChange={(e) => onChange(e.target.value)}
                      input={<OutlinedInput id="areas-chip" label="Áreas vinculadas" />}
                      renderValue={(selectedAreas) => (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                          onMouseDown={(e) => e.stopPropagation()} // evita abrir o menu ao interagir com chips
                        >
                          {selectedAreas?.map((selectedValue) => (
                            <Chip
                              key={`area-chip-${selectedValue._id}`}
                              label={selectedValue.name}
                              variant="outlined"
                              color="primary"
                              onMouseDown={(e) => e.stopPropagation()} // impede abrir menu ao clicar no "X"
                              onDelete={(e) => {
                                e.stopPropagation();
                                handleDelete(selectedValue); // remove só este
                              }}
                              sx={{
                                width: 'fit-content',
                                // se não usar visibleAreas, pode manter a linha abaixo:
                                // display: selectedValue.archived ? 'none' : '',
                              }}
                            />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {visibleAreas.map((area) => (
                        <MenuItem
                          key={area._id}
                          value={area}
                          style={{
                            // passa array de ids selecionados para destacar corretamente
                            ...getStyles(area._id, safeValue.map(v => v._id), theme),
                          }}
                        >
                          {area.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              }}
            />
          )}

          {areaLoading && !areaError && (
            <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
              <InputLabel id="areas-label" shrink>
                Áreas vinculadas
              </InputLabel>
              <InputLoader />
            </FormControl>
          )}
        </Column>
      </Row>
      <Row justifyContent="center">
        <Column xs={12} className="d-flex">
          {!systemLoading && !systemError && (
            <Controller
              control={control}
              name="systems"
              render={({ field: { onChange, value }, fieldState }) => {
                const safeValue = Array.isArray(value) ? value : [];

                const handleDelete = (selected) => {
                  const newValue = safeValue.filter(v => v._id !== selected._id);
                  onChange(newValue);
                };

                return (
                  <FormControl fullWidth sx={{ margin: '0 auto 24px' }} error={!!fieldState.error}>
                    <InputLabel id="systems-label" shrink>
                      Sistemas vinculados
                    </InputLabel>
                    <Select
                      labelId="systems-label"
                      id="systems"
                      multiple
                      notched
                      value={getSelectedContent(systems, safeValue)}
                      onChange={(e) => onChange(e.target.value)}
                      input={<OutlinedInput id="systems-chip" label="Sistemas vinculados" />}
                      renderValue={(selectedSystems) => (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                          onMouseDown={(e) => e.stopPropagation()} // evita abrir o menu ao clicar nos chips
                        >
                          {selectedSystems?.map((selectedValue) => (
                            <Chip
                              key={selectedValue._id}
                              label={selectedValue.name}
                              variant="outlined"
                              color="primary"
                              onMouseDown={(e) => e.stopPropagation()} // impede abrir o menu ao clicar no "X"
                              onDelete={(e) => {
                                e.stopPropagation();
                                handleDelete(selectedValue); // remove apenas este
                              }}
                              sx={{
                                width: 'fit-content',
                                display: selectedValue.archived ? 'none' : '',
                              }}
                            />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {systems?.map((item) => (
                        <MenuItem
                          key={item._id}
                          value={item}
                          style={{
                            // passa lista de ids selecionados para destacar corretamente
                            ...getStyles(item._id, safeValue.map(v => v._id), theme),
                            display: item.archived ? 'none' : '',
                          }}
                        >
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{fieldState.error ? fieldState.error.message : ''}</FormHelperText>
                  </FormControl>
                );
              }}
            />
          )}

          {systemLoading && !systemError && (
            <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
              <InputLabel id="systems-label" shrink>
                Sistemas vinculados
              </InputLabel>
              <InputLoader />
            </FormControl>
          )}
        </Column>
      </Row>
      <Row>
        <Column xs={12} className="d-flex">
          {!assetLoading && !assetError && (
            <Controller
              control={control}
              name="assets"
              render={({ field: { onChange, value }, fieldState }) => {
                // garante array
                const safeValue = Array.isArray(value) ? value : [];

                // opcional: esconder arquivados
                const visibleAssets = assets?.filter(a => !a.archived) ?? [];

                const handleDelete = (selected) => {
                  const newValue = safeValue.filter(v => v._id !== selected._id);
                  onChange(newValue);
                };

                return (
                  <FormControl fullWidth sx={{ margin: '0 auto 24px' }} error={!!fieldState.error}>
                    <InputLabel id="assets-label" shrink>
                      Ativos vinculados
                    </InputLabel>

                    <Select
                      labelId="assets-label"
                      id="assets"
                      multiple
                      notched
                      value={getSelectedContent(visibleAssets, safeValue)}
                      onChange={(e) => onChange(e.target.value)}
                      input={<OutlinedInput id="assets-chip" label="Ativos vinculados" />}
                      renderValue={(selectedAssets) => (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                          onMouseDown={(e) => e.stopPropagation()} // evita abrir o menu interagindo com chips
                        >
                          {selectedAssets?.map((selectedValue) => (
                            <Chip
                              key={`asset-chip-${selectedValue._id}`}
                              label={selectedValue.name}
                              variant="outlined"
                              color="primary"
                              onMouseDown={(e) => e.stopPropagation()} // impede abrir menu ao clicar no "X"
                              onDelete={(e) => {
                                e.stopPropagation();
                                handleDelete(selectedValue); // remove só este
                              }}
                              sx={{
                                width: 'fit-content',
                                // se não filtrar na origem, pode usar:
                                // display: selectedValue.archived ? 'none' : '',
                              }}
                            />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {visibleAssets.map((asset) => (
                        <MenuItem
                          key={asset._id}
                          value={asset}
                          style={{
                            // passa lista de ids selecionados para destacar corretamente
                            ...getStyles(asset._id, safeValue.map(v => v._id), theme),
                          }}
                        >
                          {asset.name}
                        </MenuItem>
                      ))}
                    </Select>

                    <FormHelperText>{fieldState.error ? fieldState.error.message : ''}</FormHelperText>
                  </FormControl>
                );
              }}
            />
          )}

          {assetLoading && !assetError && (
            <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
              <InputLabel id="assets-label" shrink>
                Ativos vinculados
              </InputLabel>
              <InputLoader />
            </FormControl>
          )}
        </Column>
      </Row>
      <Row justifyContent="end" alignItems="center">
        <Column xs={4} md={3} className="d-flex" style={{ gap: '8px', justifyContent: 'flex-end' }}>
          <ButtonForm secondary id="cancel" label="cancelar" onClick={handleBack}></ButtonForm>
          <ButtonForm label="Salvar" type="submit" />
        </Column>
      </Row>
    </form>
  );
};
